import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Quick filters si Assign filters to groups.
 *
 * Perechile vechi: `quick_filters`, `quickFiltersList`, `create_filter_form`,
 * `editFilterForm`, `saveQuickFilter`, `updateQuickFilter`, `removeFilter`,
 * `quick_filters_groups`, `filterGroupsList`, `assignGroupToFilter`,
 * `removeFilterGroup`, `editUserFilterForm`, `updateUserFilter`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

export interface KeyValue {
  value: number
  name: string
}

export interface FilterOptions {
  types: Array<{ ID: number; type_name: string }>
  statuses: KeyValue[]
  priorities: KeyValue[]
  categories: KeyValue[]
  /** Grupate pe numele categoriei, ca in `getSubcategoriesGrouped()`. */
  subcategories: Record<string, Array<{ ID: number; subcategory_name: string }>>
  escalation_types: Array<{ escalation_type: number; type_name: string }>
  groups: Array<{ ID: number; department_name: string }>
}

/** Cele opt campuri care alcatuiesc un filtru. */
export interface FilterValues {
  type?: string[]
  status?: string[]
  escalation_type?: string[]
  priority?: string[]
  category?: string[]
  subcategory?: string[]
  group?: string[]
  advanced?: string
}

export interface QuickFilterRow {
  ID: number
  filter_name: string
}

export interface FilterGroupsRow extends QuickFilterRow {
  groups: Array<{ ID: number; group_name: string }>
}

const BASE = '/settings/easeedesk/quick_filters'
const GROUPS = '/settings/easeedesk/quick_filters_groups'
const USER = '/settings/easeedesk/user_filters'

export function quickFiltersShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'quick_filters'],
    queryFn: () => apiGet<{ title: string; moduleUrl: string }>(BASE),
  })
}

export function quickFiltersListQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'quick_filters', 'list'],
    queryFn: () => apiGet<{ filters: QuickFilterRow[] }>(`${BASE}/list`),
  })
}

/** Listele formularului. `categories` = categoriile deja alese, pentru subcategorii. */
export function filterOptionsQuery(categories: string[] = []) {
  const query = categories.map((c) => `category[]=${encodeURIComponent(c)}`).join('&')
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'quick_filters', 'options', categories.join(',')],
    queryFn: () => apiGet<FilterOptions>(`${BASE}/options${query === '' ? '' : `?${query}`}`),
  })
}

export function quickFilterQuery(filter: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'quick_filters', 'one', filter],
    queryFn: () =>
      apiGet<{
        ID: number
        filter_name: string
        filter: FilterValues
        subcategories: FilterOptions['subcategories']
      }>(`${BASE}/${filter}`),
    enabled: filter > 0,
  })
}

function body(name: string, values: FilterValues) {
  return {
    'filter-name': name,
    'type[]': values.type ?? [],
    'status[]': values.status ?? [],
    'escalation_type[]': values.escalation_type ?? [],
    'priority[]': values.priority ?? [],
    'category[]': values.category ?? [],
    'subcategory[]': values.subcategory ?? [],
    'group[]': values.group ?? [],
    advanced: values.advanced ?? '',
  }
}

export function createQuickFilter(name: string, values: FilterValues) {
  return apiPost<{ ok?: boolean; ID?: number }>(BASE, body(name, values))
}

export function updateQuickFilter(filter: number, name: string, values: FilterValues) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${filter}`, body(name, values))
}

export function removeQuickFilter(filter: number) {
  return apiPost<{ ok?: boolean }>(`${BASE}/${filter}/remove`, {})
}

export function filterGroupsShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'quick_filters_groups'],
    queryFn: () =>
      apiGet<{
        title: string
        moduleUrl: string
        groups: Array<{ ID: number; department_name: string }>
        filters: FilterGroupsRow[]
      }>(GROUPS),
  })
}

export function assignFilterGroups(filter: number, groupIds: number[]) {
  return apiPost<{ ok?: boolean }>(`${GROUPS}/${filter}`, { 'filter-groups[]': groupIds })
}

export function removeFilterGroup(filter: number, group: number) {
  return apiPost<{ ok?: boolean }>(`${GROUPS}/${filter}/remove_group`, { group })
}

export function userFilterQuery(filter: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'user_filters', filter],
    queryFn: () =>
      apiGet<{
        ID: number
        filter_name: string
        type: string
        filter: FilterValues
        subcategories: FilterOptions['subcategories']
      }>(`${USER}/${filter}`),
    enabled: filter > 0,
  })
}

export function updateUserFilter(filter: number, name: string, values: FilterValues) {
  return apiPost<{ ok?: boolean }>(`${USER}/${filter}`, body(name, values))
}
